import React from "react";
import { motion } from "framer-motion";
import { Mail, Users, Database, CreditCard, FileText, Cable } from "lucide-react";
import { Container, Section, Eyebrow, DisplayHeading, BodyCopy, Badge, StatusBadge, Reveal } from "@/components/kit";
import { fadeUp, stagger, viewport } from "@/lib/motion";

const LIFECYCLE = ["Assess", "Change", "Re-test", "Find", "Remediate", "Re-verify"];

const CONNECTORS = [
  { label: "Email", icon: Mail, perm: "Send" },
  { label: "CRM", icon: Users, perm: "Read/Write" },
  { label: "Database", icon: Database, perm: "Read" },
  { label: "Payments", icon: CreditCard, perm: "Blocked" },
  { label: "Files", icon: FileText, perm: "Read" },
  { label: "APIs", icon: Cable, perm: "Scoped" },
];

export default function NextState() {
  return (
    <Section id="next-state" className="border-t border-line bg-panel-raised/30">
      <Container>
        <div className="max-w-3xl">
          <Reveal><Eyebrow>The next state of AI assurance</Eyebrow></Reveal>
          <DisplayHeading className="mt-6 text-3xl leading-[1.1] sm:text-4xl md:text-5xl">
            AI changes.<br /><span className="text-content-faint">And increasingly, AI acts.</span>
          </DisplayHeading>
        </div>

        <div className="mt-10 grid gap-5 sm:mt-14 sm:gap-6 lg:grid-cols-2">
          {/* A — continuous */}
          <Reveal>
            <div className="flex h-full flex-col rounded-lg border border-line bg-panel-raised p-5 sm:p-8">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-soft">Continuous assurance</div>
              <h3 className="mt-3 font-display text-2xl font-medium text-content">Its assurance should change with it.</h3>
              <BodyCopy className="mt-3 !text-[15px]">
                Assurance moves from a one-time snapshot toward an ongoing, evidence-backed state.
              </BodyCopy>
              <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={viewport} className="mt-6 flex flex-wrap items-center gap-x-1.5 gap-y-2">
                {LIFECYCLE.map((s, i) => (
                  <React.Fragment key={s}>
                    <motion.span variants={fadeUp} className="rounded-full border border-line bg-panel px-3 py-1.5 font-mono text-[11px] uppercase tracking-wide text-content">{s}</motion.span>
                    {i < LIFECYCLE.length - 1 && <span className="text-content-faint">→</span>}
                  </React.Fragment>
                ))}
              </motion.div>
              <div className="mt-6"><Badge tone="brand">In rollout · availability varies by plan</Badge></div>
            </div>
          </Reveal>

          {/* B — agentic */}
          <Reveal i={1}>
            <div className="flex h-full flex-col rounded-lg border border-line bg-panel-raised p-5 sm:p-8">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand">Agentic governance</div>
              <h3 className="mt-3 font-display text-2xl font-medium text-content">Govern what it can do.</h3>
              <BodyCopy className="mt-3 !text-[15px]">
                As AI takes actions, governance must understand what an agent can access — and what
                happens when it goes wrong.
              </BodyCopy>
              <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={viewport} className="mt-6 grid grid-cols-2 gap-2.5 sm:grid-cols-3">
                {CONNECTORS.map((c) => {
                  const blocked = c.perm === "Blocked";
                  return (
                    <motion.div key={c.label} variants={fadeUp} className={`flex items-center gap-2.5 rounded-md border p-3 ${blocked ? "border-danger/30 bg-danger/5" : "border-line bg-panel"}`}>
                      <c.icon size={16} strokeWidth={1.5} className={blocked ? "text-danger" : "text-brand-soft"} />
                      <div className="min-w-0">
                        <div className="truncate text-[12px] font-medium text-content">{c.label}</div>
                        <div className={`font-mono text-[9px] uppercase tracking-wide ${blocked ? "text-danger" : "text-content-faint"}`}>{c.perm}</div>
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
              <div className="mt-6"><Badge tone="brand">Guardrail testing available · blast-radius analysis in development</Badge></div>
            </div>
          </Reveal>
        </div>
      </Container>
    </Section>
  );
}
